<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-600 text-sm">
      <span class="font-semibold cursor-pointer" @click="router.push('/')">
        Home
      </span>
      <span class="mx-2">/</span>
      <span class="font-semibold cursor-pointer" @click="router.push({ name: 'Partners' })">
        Partners
      </span>
      <span class="mx-2">/</span>
      <span class="text-gray-800 font-bold">Add Partner</span>
    </nav>

    <h3 class="font-bold text-2xl">Add Partner</h3>

    <!-- Errors box -->
    <div
      v-if="errors.length"
      class="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm"
    >
      <ul class="list-disc list-inside">
        <li v-for="(err, idx) in errors" :key="idx">{{ err }}</li>
      </ul>
    </div>

    <!-- Form -->
    <form
      @submit.prevent="submitForm"
      class="bg-white shadow rounded-lg p-6 flex flex-col gap-6"
    >
      <!-- Name & Website -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Name -->
        <div>
          <label class="block text-sm font-medium mb-1">Partner Name</label>
          <input
            v-model="form.name"
            type="text"
            class="border border-gray-300 rounded-md w-full px-4 py-2"
            placeholder="Ex: Oman Arab Bank"
          />
        </div>

        <!-- Website -->
        <div>
          <label class="block text-sm font-medium mb-1">Website URL</label>
          <input
            v-model="form.website_url"
            type="url"
            class="border border-gray-300 rounded-md w-full px-4 py-2"
            placeholder="https://www.example.com"
          />
        </div>
      </div>

      <!-- Sort order & Logo -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Sort Order -->
        <div>
          <label class="block text-sm font-medium mb-1">Sort Order</label>
          <input
            v-model.number="form.sort_order"
            type="number"
            min="0"
            class="border border-gray-300 rounded-md w-full px-4 py-2"
            placeholder="1"
          />
          <p class="text-xs text-gray-500 mt-1">
            Smaller numbers appear first in the list (default: 0).
          </p>
        </div>

        <!-- Logo -->
        <div>
          <label class="block text-sm font-medium mb-1">Logo (optional)</label>
          <input
            type="file"
            accept="image/*"
            @change="onLogoChange"
            class="border border-gray-300 rounded-md w-full px-4 py-2 bg-white"
          />
          <p class="text-xs text-gray-500 mt-1">
            Supported formats: jpeg, png, jpg, gif, svg, webp. Max size: 2MB.
          </p>
          <div v-if="logoPreview" class="mt-2">
            <p class="text-xs text-gray-600 mb-1">Preview:</p>
            <img
              :src="logoPreview"
              alt="Logo preview"
              class="w-16 h-16 object-contain border rounded-md"
            />
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-3 mt-4">
        <button
          type="button"
          class="px-4 py-2 border rounded-md"
          @click="router.push({ name: 'Partners' })"
          :disabled="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
          :disabled="isSubmitting"
        >
          <span v-if="isSubmitting">Saving...</span>
          <span v-else>Save</span>
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import apiClient from "@/apiClient";
import { useToast } from "vue-toastification";

const router = useRouter();
const toast = useToast();

const form = ref({
  name: "",
  website_url: "",
  sort_order: 0,
});

const logoFile = ref(null);
const logoPreview = ref(null);

const errors = ref([]);
const isSubmitting = ref(false);

function onLogoChange(event) {
  const file = event.target.files[0];
  if (!file) {
    logoFile.value = null;
    logoPreview.value = null;
    return;
  }

  logoFile.value = file;
  logoPreview.value = URL.createObjectURL(file);
}

function validateForm() {
  errors.value = [];

  if (!form.value.name.trim()) {
    errors.value.push("Partner name is required.");
  }

  if (form.value.website_url && !isValidUrl(form.value.website_url)) {
    errors.value.push("Website URL is not valid.");
  }

  return errors.value.length === 0;
}

function isValidUrl(value) {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

async function submitForm() {
  try {
    if (!validateForm()) return;
    isSubmitting.value = true;

    const fd = new FormData();
    fd.append("name", form.value.name);
    if (form.value.website_url) {
      fd.append("website_url", form.value.website_url);
    }
    fd.append("sort_order", form.value.sort_order ?? 0);

    if (logoFile.value) {
      fd.append("logo", logoFile.value);
    }

    const res = await apiClient.post("/admin/partners", fd, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    console.log("Partner created:", res.data);
    toast.success("Partner added successfully!");
    router.push({ name: "Partners" });
  } catch (error) {
    console.error("Error adding partner:", error);
    errors.value = [];

    if (error.response?.data) {
      const data = error.response.data;
      if (data.message && typeof data.message === "string") {
        errors.value.push(data.message);
      }
      if (data.errors) {
        Object.values(data.errors).forEach((msgArr) =>
          msgArr.forEach((msg) => errors.value.push(msg))
        );
      }
    } else {
      errors.value.push("Failed to add partner. Please try again.");
    }
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<style scoped>
</style>
